import * as React from 'react';
import { authStorage, decodeJwt, isTokenExpired, type DecodedAdminJwt } from '@/lib/authStorage';

export interface AuthUser {
  email: string;
  name?: string;
}

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  user: AuthUser | null;
  status: AuthStatus;
  loginWithToken: (token: string) => boolean;
  logout: () => void;
  startGoogleLogin: (returnTo?: string) => void;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

function userFromDecoded(decoded: DecodedAdminJwt | null): AuthUser | null {
  if (!decoded) return null;
  return { email: decoded.email, name: typeof decoded.name === 'string' ? decoded.name : undefined };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = React.useState<AuthStatus>('loading');
  const [user, setUser] = React.useState<AuthUser | null>(null);

  React.useEffect(() => {
    const token = authStorage.getToken();
    if (!token) {
      setStatus('unauthenticated');
      return;
    }
    const decoded = decodeJwt(token);
    if (!decoded || isTokenExpired(decoded)) {
      authStorage.clearToken();
      setStatus('unauthenticated');
      return;
    }
    setUser(userFromDecoded(decoded));
    setStatus('authenticated');
  }, []);

  const loginWithToken = React.useCallback((token: string) => {
    const decoded = decodeJwt(token);
    if (!decoded || isTokenExpired(decoded)) return false;
    authStorage.setToken(token);
    setUser(userFromDecoded(decoded));
    setStatus('authenticated');
    return true;
  }, []);

  const logout = React.useCallback(() => {
    authStorage.clearToken();
    setUser(null);
    setStatus('unauthenticated');
    window.location.assign('/login');
  }, []);

  const startGoogleLogin = React.useCallback((returnTo?: string) => {
    if (returnTo) authStorage.setReturnTo(returnTo);
    const loginUrl = import.meta.env.VITE_ADMIN_AUTH_LOGIN_URL;
    if (!loginUrl) {
      console.error('VITE_ADMIN_AUTH_LOGIN_URL is not configured');
      return;
    }
    window.location.assign(loginUrl);
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({ user, status, loginWithToken, logout, startGoogleLogin }),
    [user, status, loginWithToken, logout, startGoogleLogin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
